import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Shield, Check, Heart, MessageSquare, Users, Briefcase, Home, HandHeart, ChevronRight, Pen } from "lucide-react";
import { LeadMagnet, useLeadMagnetState, LockedBadge } from "../components/LeadMagnet";

const boundaryAssessment = [
  { area: "Work", questions: [
    "I say yes to extra projects even when I'm overwhelmed.",
    "I answer work emails/calls on my personal time.",
    "I feel guilty leaving on time.",
    "I apologize for things that aren't my fault at work.",
  ]},
  { area: "Family", questions: [
    "I let family members criticize my choices without speaking up.",
    "I feel responsible for other people's emotions.",
    "I over-explain my decisions to get approval.",
    "I drop everything when a family member calls, even if it's not urgent.",
  ]},
  { area: "Friends & Social", questions: [
    "I agree to plans I don't want to attend.",
    "I let people vent to me even when I don't have the energy.",
    "I lend money or things even when I know I won't get them back.",
    "I laugh at jokes that make me uncomfortable.",
  ]},
  { area: "Self", questions: [
    "I put everyone else's needs before my own — every time.",
    "I feel selfish when I take time for myself.",
    "I ignore my own exhaustion to keep helping others.",
    "I don't speak up when something bothers me.",
  ]},
];

const scripts = [
  {
    id: 1,
    category: "Work",
    icon: <Briefcase className="w-5 h-5" />,
    situation: "Your boss asks you to stay late — again",
    peoplePleaser: "\"Sure, no problem! I'll stay as long as you need.\" (while screaming inside)",
    boundary: "\"I have a commitment this evening that I can't move. I can prioritize this first thing tomorrow morning — would that work?\"",
    softer: "\"I can give you 30 more minutes today, but I'll need to pick this up fresh tomorrow to do my best work.\"",
    affirmation: "I GET TO protect my time. My evenings belong to me.",
    note: "You don't owe anyone an explanation for your 'commitment.' A commitment to yourself counts.",
  },
  {
    id: 2,
    category: "Work",
    icon: <Briefcase className="w-5 h-5" />,
    situation: "A coworker keeps dumping their tasks on you",
    peoplePleaser: "\"Oh, it's fine, I'll just handle it.\" (adds it to your already full plate)",
    boundary: "\"I appreciate you thinking of me, but I'm at capacity with my own projects right now. You might want to check with [manager] about reprioritizing.\"",
    softer: "\"I wish I could help, but I have to protect my current deadlines. Let me know if there's a small, specific thing I can do.\"",
    affirmation: "I GET TO focus on my own work without guilt.",
    note: "Their poor planning is not your emergency.",
  },
  {
    id: 3,
    category: "Work",
    icon: <Briefcase className="w-5 h-5" />,
    situation: "Someone contacts you about work on your day off",
    peoplePleaser: "\"Let me just quickly handle this...\" (goodbye, weekend)",
    boundary: "\"I'm off today and won't be checking messages. I'll follow up on Monday.\"",
    softer: "\"I'll flag this for Monday morning — it'll be the first thing I look at.\"",
    affirmation: "I GET TO rest without guilt. Rest makes me better, not lazy.",
    note: "You can set this as an auto-reply. Let the boundary do the work for you.",
  },
  {
    id: 4,
    category: "Work",
    icon: <Briefcase className="w-5 h-5" />,
    situation: "Asked to volunteer your expertise for free",
    peoplePleaser: "\"Sure, I'd love to help!\" (while resenting every minute)",
    boundary: "\"Thank you for thinking of me! My rate for that type of work is [amount]. Would you like to set something up?\"",
    softer: "\"I'm not able to take that on for free, but I'd be happy to point you toward some great resources.\"",
    affirmation: "I GET TO value my skills. My expertise has worth.",
    note: "If they wanted it for free, they didn't value it. That's not your problem to solve.",
  },
  {
    id: 5,
    category: "Family",
    icon: <Home className="w-5 h-5" />,
    situation: "A family member criticizes your life choices",
    peoplePleaser: "\"You're right, maybe I should...\" (abandons own decision)",
    boundary: "\"I hear your concern, and I appreciate you caring. This is what feels right for me right now. I'm not looking for feedback on this one.\"",
    softer: "\"I know you mean well. I've thought about this a lot, and I'm comfortable with my decision.\"",
    affirmation: "I GET TO make my own choices. I don't need permission to live my life.",
    note: "You can love someone deeply and still not accept their opinion as your truth.",
  },
  {
    id: 6,
    category: "Family",
    icon: <Home className="w-5 h-5" />,
    situation: "A parent guilt-trips you for not visiting/calling enough",
    peoplePleaser: "\"I'm sorry, I'm the worst. I'll come this weekend.\" (cancels your own plans)",
    boundary: "\"I love you and I love spending time with you. I'm doing my best to balance everything. Let's plan a time that works for both of us.\"",
    softer: "\"I miss you too. How about we set a regular day to call so we both have something to look forward to?\"",
    affirmation: "I GET TO love my family AND have my own life. Both can be true.",
    note: "Guilt is not love. Love doesn't require you to shrink.",
  },
  {
    id: 7,
    category: "Family",
    icon: <Home className="w-5 h-5" />,
    situation: "Relatives ask intrusive personal questions",
    peoplePleaser: "Answers the question uncomfortably, feels exposed for days after.",
    boundary: "\"That's something I'm keeping private for now. So, tell me about [redirect topic]!\"",
    softer: "\"Ha! That's a big question. I'm not ready to share on that yet. How's YOUR [topic]?\"",
    affirmation: "I GET TO keep parts of my life private. Privacy is not secrecy.",
    note: "A redirect with warmth is still a boundary. You don't need to be harsh to be firm.",
  },
  {
    id: 8,
    category: "Family",
    icon: <Home className="w-5 h-5" />,
    situation: "Family expects you to host/organize every event",
    peoplePleaser: "\"I'll handle everything, don't worry about it.\" (then cries in the bathroom)",
    boundary: "\"I'd love to be part of this, but I can't host this time. Who else can take the lead? I'm happy to bring a dish.\"",
    softer: "\"I can handle [specific small task], but I need someone else to take point on the rest this time.\"",
    affirmation: "I GET TO participate without being in charge. Showing up is enough.",
    note: "If no one else steps up when you stop, that tells you everything about the pattern.",
  },
  {
    id: 9,
    category: "Family",
    icon: <Home className="w-5 h-5" />,
    situation: "Someone shares your personal business without permission",
    peoplePleaser: "Says nothing. Quietly stops sharing anything personal. Feels betrayed.",
    boundary: "\"I shared that with you in confidence. It's important to me that private things stay private. Can I count on that going forward?\"",
    softer: "\"Hey, I noticed [person] knew about [thing]. I'd prefer to be the one to share my own news. Can we agree on that?\"",
    affirmation: "I GET TO decide who knows my story. My life is mine to share.",
    note: "This boundary teaches people how to earn your trust. It's a gift, not a punishment.",
  },
  {
    id: 10,
    category: "Friends",
    icon: <Users className="w-5 h-5" />,
    situation: "A friend only calls when they need something",
    peoplePleaser: "Drops everything to help, again. Wonders why they feel drained after.",
    boundary: "\"I've noticed our conversations mostly happen when you need help. I value our friendship and I'd love to hear from you when things are going well too.\"",
    softer: "\"I want to help, and I also miss just hanging out. Can we plan something fun soon — no agenda?\"",
    affirmation: "I GET TO have friendships that fill me up, not drain me.",
    note: "This conversation might change the friendship. That's okay. You deserve reciprocity.",
  },
  {
    id: 11,
    category: "Friends",
    icon: <Users className="w-5 h-5" />,
    situation: "Pressured to go out when you need rest",
    peoplePleaser: "\"Okay fine, I'll come for a little bit.\" (regrets it the entire time)",
    boundary: "\"I'm staying in tonight — I need the rest. Have the best time! Send me pics.\"",
    softer: "\"Not tonight, but I don't want to miss the next one! Let's plan something for next week.\"",
    affirmation: "I GET TO rest. Choosing rest is not choosing isolation.",
    note: "No is a complete sentence. But if you want to soften it, the redirect works beautifully.",
  },
  {
    id: 12,
    category: "Friends",
    icon: <Users className="w-5 h-5" />,
    situation: "Someone wants you to participate in gossip",
    peoplePleaser: "Listens, nods, maybe even adds to it, then feels awful.",
    boundary: "\"I'm trying not to talk about people when they're not here. Have you talked to them about it directly?\"",
    softer: "\"Hmm, I don't know enough about their situation to have an opinion. I hope it works out for them.\"",
    affirmation: "I GET TO protect other people's dignity, even when they're not in the room.",
    note: "This boundary protects you too — if they gossip about others to you, they gossip about you to others.",
  },
  {
    id: 13,
    category: "Friends",
    icon: <Users className="w-5 h-5" />,
    situation: "Someone borrows money or things and never returns them",
    peoplePleaser: "\"Oh, don't worry about it...\" (worries about it constantly)",
    boundary: "\"Hey, I wanted to check in about the [money/item]. When can I expect that back? I need it by [date].\"",
    softer: "\"No pressure, but I do need [item/amount] back. Can we figure out a timeline?\"",
    affirmation: "I GET TO ask for what's mine. Expecting reciprocity is not selfish.",
    note: "Going forward: only lend what you're okay never getting back. That removes the resentment.",
  },
  {
    id: 14,
    category: "Relationship",
    icon: <Heart className="w-5 h-5" />,
    situation: "Your partner dismisses your feelings",
    peoplePleaser: "\"You're right, I'm probably overreacting.\" (stuffs feelings down)",
    boundary: "\"When you say I'm overreacting, it makes me feel like my feelings don't matter to you. I need you to hear me, even if you don't fully understand.\"",
    softer: "\"I know it might not seem like a big deal to you, but it is to me. Can we talk about it?\"",
    affirmation: "I GET TO have feelings that are honored. My emotions are valid.",
    note: "If someone consistently dismisses your feelings, that's information about the relationship.",
  },
  {
    id: 15,
    category: "Relationship",
    icon: <Heart className="w-5 h-5" />,
    situation: "You need alone time and feel guilty about it",
    peoplePleaser: "Never asks. Slowly burns out. Gets resentful.",
    boundary: "\"I love our time together, AND I need some time alone to recharge. It's not about you — it's about me being a better partner when we are together.\"",
    softer: "\"I'm going to take [time period] for myself today. I'll be recharged and present when I'm back.\"",
    affirmation: "I GET TO need space. Solitude is not rejection.",
    note: "The right person will respect your need for space. The wrong person will punish you for it.",
  },
  {
    id: 16,
    category: "Social",
    icon: <MessageSquare className="w-5 h-5" />,
    situation: "Someone keeps interrupting you mid-sentence",
    peoplePleaser: "Stops talking. Lets them take over. Swallows their words.",
    boundary: "\"Hold on — I wasn't finished. I'd like to complete my thought.\"",
    softer: "\"One sec — let me finish this part, then I want to hear your take.\"",
    affirmation: "I GET TO be heard. My words matter.",
    note: "Say it calmly, with a small smile. It's powerful because it's not aggressive — it's clear.",
  },
  {
    id: 17,
    category: "Social",
    icon: <MessageSquare className="w-5 h-5" />,
    situation: "Someone comments on your body, food, or appearance",
    peoplePleaser: "Laughs it off. Goes home and thinks about it for 3 days.",
    boundary: "\"I'd prefer not to have comments about my body/food. Thanks for understanding.\"",
    softer: "\"Ha — yeah, I'm not really into body talk. Did you see [redirect]?\"",
    affirmation: "I GET TO exist in my body without commentary. My body is not a discussion topic.",
    note: "You don't have to educate them. A simple redirect protects your peace.",
  },
  {
    id: 18,
    category: "Social",
    icon: <MessageSquare className="w-5 h-5" />,
    situation: "Asked to keep a secret that feels wrong or heavy",
    peoplePleaser: "Carries the secret. Loses sleep. Feels trapped.",
    boundary: "\"I don't feel comfortable keeping this to myself. If it involves someone's safety or wellbeing, I might need to share it with the right person.\"",
    softer: "\"That's a lot to carry. Have you considered talking to someone who can actually help?\"",
    affirmation: "I GET TO put down burdens that aren't mine to carry.",
    note: "There's a difference between a confidence and a burden. You're allowed to know the difference.",
  },
  {
    id: 19,
    category: "Social",
    icon: <MessageSquare className="w-5 h-5" />,
    situation: "You need to leave a conversation or gathering",
    peoplePleaser: "Stays 2 hours past when they wanted to leave. Fabricates elaborate excuses.",
    boundary: "\"This has been wonderful. I'm going to head out now. Thank you for a great time.\"",
    softer: "\"I'm going to slip out — early morning tomorrow. So glad I came!\"",
    affirmation: "I GET TO leave when I'm ready. I don't need to perform staying.",
    note: "Practice this one. It gets easier every time. No one is thinking about your exit as much as you are.",
  },
  {
    id: 20,
    category: "Self",
    icon: <HandHeart className="w-5 h-5" />,
    situation: "You catch yourself over-apologizing",
    peoplePleaser: "\"Sorry! Sorry. I'm sorry I'm always sorry.\"",
    boundary: "Replace 'I'm sorry' with 'Thank you.' Instead of 'Sorry I'm late' — 'Thank you for waiting.' Instead of 'Sorry for venting' — 'Thank you for listening.'",
    softer: "Start with just ONE day of catching yourself. Keep a tally. Awareness is the first boundary.",
    affirmation: "I GET TO take up space without apologizing for existing.",
    note: "This one isn't about a conversation with someone else. It's the boundary you set with your own inner critic.",
  },
];

const iGetToAffirmations = [
  "I GET TO say no. No is a complete sentence.",
  "I GET TO disappoint people and still be a good person.",
  "I GET TO choose me without feeling selfish.",
  "I GET TO set boundaries even when people don't understand them.",
  "I GET TO change my mind. I owe no one consistency at the expense of my peace.",
  "I GET TO walk away from conversations that drain me.",
  "I GET TO require respect, not just hope for it.",
  "I GET TO be kind AND firm. They are not opposites.",
  "I GET TO protect my energy. It is a finite resource.",
  "I GET TO outgrow relationships that no longer serve me.",
  "I GET TO speak up, even when my voice shakes.",
  "I GET TO love people from a distance if that's what's healthiest.",
];

const familyConversationStarters = [
  {
    topic: "Opening the Conversation",
    script: "\"There's something I've been wanting to talk about. It's important to me, and I need you to hear me out before responding. Can you do that for me?\"",
    tip: "Choose a calm, private moment. Not during a fight, not in front of others.",
  },
  {
    topic: "Naming the Pattern",
    script: "\"I've noticed that when [specific pattern], I end up feeling [specific emotion]. I don't think you intend that, but I want us to find a better way.\"",
    tip: "Use 'I noticed' and 'I feel' — not 'You always' or 'You never.' Specifics beat generalizations.",
  },
  {
    topic: "Stating Your Need",
    script: "\"What I need going forward is [specific need]. I know this might feel different, but it's what I need to show up as my best self in this relationship.\"",
    tip: "Be specific. 'I need space' is vague. 'I need 30 minutes to decompress when I get home before talking about the day' is clear.",
  },
  {
    topic: "When They Push Back",
    script: "\"I understand this is hard to hear. I'm not asking you to agree with it — I'm asking you to respect it. This boundary exists because I love us, not because I'm trying to hurt you.\"",
    tip: "Expect pushback. Stay calm. Repeat your boundary once, then end the conversation if needed.",
  },
  {
    topic: "Holding the Line",
    script: "\"I've already shared how I feel about this, and my answer hasn't changed. I love you, and this is where I stand.\"",
    tip: "You don't need a new argument each time. Repetition IS the boundary.",
  },
  {
    topic: "Acknowledging Progress",
    script: "\"I noticed you [respected my boundary]. That meant so much to me. Thank you for hearing me.\"",
    tip: "Reinforce the behavior you want to see more of. Boundaries aren't only about the hard conversations.",
  },
];

const categoryColors = {
  Work: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-800", accent: "bg-blue-600" },
  Family: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", accent: "bg-amber-600" },
  Friends: { bg: "bg-green-50", border: "border-green-200", text: "text-green-800", accent: "bg-green-600" },
  Relationship: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-800", accent: "bg-rose-600" },
  Social: { bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-800", accent: "bg-purple-600" },
  Self: { bg: "bg-teal-50", border: "border-teal-200", text: "text-teal-800", accent: "bg-teal-600" },
};

const ScriptCard = ({ script, locked }) => {
  const colors = categoryColors[script.category] || categoryColors.Social;
  return (
    <div className="print:page-break-inside-avoid mb-8 relative" data-testid={`script-card-${script.id}`}>
      <div className={`${colors.bg} ${colors.border} border-2 rounded-2xl overflow-hidden ${locked ? "blur-sm select-none pointer-events-none print:blur-0" : ""}`}>
        <div className={`${colors.accent} px-6 py-3 flex items-center gap-3`}>
          <span className="text-white">{script.icon}</span>
          <span className="text-white/80 text-xs font-bold uppercase tracking-widest">{script.category}</span>
          <span className="text-white text-xs ml-auto font-bold">#{script.id}</span>
        </div>
        <div className="p-6 md:p-8">
          <h3 className={`text-lg font-bold ${colors.text} mb-5`}>Situation: {script.situation}</h3>

          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-red-400 text-xs font-bold uppercase tracking-widest mb-1">The People-Pleasing Response</p>
              <p className="text-red-700 text-sm italic">{script.peoplePleaser}</p>
            </div>

            <div className="bg-[#F0FDF4] border-2 border-[#25a1c2]/30 rounded-xl p-4">
              <p className="text-[#25a1c2] text-xs font-bold uppercase tracking-widest mb-1">What You GET TO Say Instead</p>
              <p className="text-[#068fa2] font-medium">{script.boundary}</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-1">Softer Stepping Stone</p>
              <p className="text-gray-700 text-sm">{script.softer}</p>
            </div>

            <div className="bg-[#068fa2] rounded-xl p-4">
              <p className="text-[#5EEAD4] text-xs font-bold uppercase tracking-widest mb-1">Your Affirmation</p>
              <p className="text-white font-serif italic text-lg">"{script.affirmation}"</p>
            </div>

            <div className="flex items-start gap-2 mt-2">
              <Pen className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <p className="text-gray-500 text-xs italic">{script.note}</p>
            </div>

            <div className="border-t border-dashed border-gray-300 pt-4 mt-4">
              <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-2">Make It Yours — Rewrite in Your Voice:</p>
              <div className="h-16 border border-dashed border-gray-300 rounded-lg bg-white" />
            </div>
          </div>
        </div>
      </div>
      {locked && <LockedBadge label="Unlock all 20 scripts" />}
    </div>
  );
};

export default function BoundaryBuilder() {
  const { unlocked, markUnlocked } = useLeadMagnetState("boundary-builder-unlocked");

  useEffect(() => {
    document.title = "The Boundary Builder — Relationship Workbook | Mind Body Spirit HYGIENE™ Tools";
  }, []);

  return (
    <div className="bg-[#efeae7] min-h-screen print:bg-white" data-testid="boundary-builder-page">
      <div className="print:hidden fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-[#CCFBF1]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-[#25a1c2] hover:text-[#068fa2] text-sm font-medium" data-testid="back-to-shop-link">
            &larr; Back to Shop
          </Link>
          <button onClick={() => window.print()} className="bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-full px-6 py-2.5 text-sm font-bold" data-testid="print-btn">
            Print / Save as PDF
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 pt-24 pb-16 print:pt-8 print:px-8">
        {/* Cover Page */}
        <div className="text-center mb-16 print:mb-10 print:page-break-after" data-testid="cover-section">
          <div className="w-20 h-20 bg-[#25a1c2] rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <p className="text-[#25a1c2] text-sm font-bold tracking-widest uppercase mb-3">A Mind Body Spirit HYGIENE™ Practice</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#042F2E] tracking-tight mb-4" data-testid="workbook-title">The Boundary Builder</h1>
          <p className="text-xl text-[#475569] mb-2">Boundary HYGIENE™ — Real tools, for real women, in the messy middle.</p>
          <p className="text-[#25a1c2] font-medium">By Temple Franklin & the Divine Team</p>

          <div className="mt-8 bg-[#F0F5F4] border border-[#CCFBF1] rounded-xl p-5 max-w-lg mx-auto text-left">
            <p className="text-[#068fa2] text-sm leading-relaxed">
              This workbook is for you — the people pleaser, the empath, the one who always puts others first. These are not rules. They are <strong>templates</strong>. Read them, practice them, and then <strong>rewrite them in your own voice</strong>. The goal isn't to become a different person. The goal is to become the person you already are — with the language to match.
            </p>
          </div>

          <div className="mt-6 flex items-center justify-center gap-6 text-sm text-[#475569] flex-wrap">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#25a1c2]" />
              <span>20 boundary scripts</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#25a1c2]" />
              <span>Boundary assessment</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#25a1c2]" />
              <span>Family conversation guide</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#25a1c2]" />
              <span>12 "I GET TO" affirmations</span>
            </div>
          </div>
        </div>

        {/* PART 1: Boundary Assessment */}
        <div className="mb-16 print:page-break-before" data-testid="assessment-section">
          <div className="bg-[#042F2E] rounded-2xl p-6 mb-8">
            <p className="text-[#5EEAD4] text-sm font-bold uppercase tracking-widest mb-1">Part 1</p>
            <h2 className="text-white text-2xl md:text-3xl font-serif">Where Are My Boundaries Weak?</h2>
            <p className="text-white/60 text-sm mt-2">Be honest. No one sees this but you. Check every box that feels true.</p>
          </div>

          <div className="space-y-8">
            {boundaryAssessment.map((section, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 md:p-8 border border-[#CCFBF1] shadow-sm">
                <h3 className="font-serif text-xl text-[#25a1c2] font-bold mb-4">{section.area} Boundaries</h3>
                <div className="space-y-3">
                  {section.questions.map((q, j) => (
                    <div key={j} className="flex items-start gap-3">
                      <div className="w-5 h-5 mt-0.5 border-2 border-[#25a1c2] rounded shrink-0" />
                      <span className="text-[#068fa2] text-sm">{q}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-dashed border-[#CCFBF1]">
                  <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-2">My biggest struggle in {section.area.toLowerCase()} boundaries:</p>
                  <div className="h-12 border border-dashed border-gray-300 rounded-lg" />
                </div>
              </div>
            ))}
          </div>

          <div className="bg-[#F0FDF4] border-2 border-[#25a1c2]/20 rounded-2xl p-6 md:p-8 mt-8">
            <h3 className="font-serif text-xl text-[#25a1c2] mb-3">Your Score — What It Means</h3>
            <div className="space-y-3 text-sm text-[#068fa2]">
              <p><strong>0-4 checks:</strong> You have solid boundaries in most areas. Focus on the specific areas you checked.</p>
              <p><strong>5-9 checks:</strong> Your boundaries need strengthening in certain relationships. The scripts in this workbook will give you the exact language you need.</p>
              <p><strong>10-16 checks:</strong> You are giving yourself away. This is not selflessness — this is self-abandonment. This workbook was made for you. Go slowly. Start with ONE script this week.</p>
            </div>
            <div className="mt-4 bg-white rounded-xl p-4 border border-[#CCFBF1]">
              <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-2">I checked ______ boxes. The area I want to work on first:</p>
              <div className="h-10 border border-dashed border-gray-300 rounded-lg" />
            </div>
          </div>
        </div>

        {/* PART 2: The 20 Boundary Scripts */}
        <div className="mb-16 print:page-break-before" data-testid="scripts-section">
          <div className="bg-[#25a1c2] rounded-2xl p-6 mb-4">
            <p className="text-[#5EEAD4] text-sm font-bold uppercase tracking-widest mb-1">Part 2</p>
            <h2 className="text-white text-2xl md:text-3xl font-serif">20 Boundary Scripts</h2>
            <p className="text-white/60 text-sm mt-2">Exact words for 20 common situations. Use them as-is, or rewrite them in your own voice at the bottom of each card.</p>
          </div>
          <p className="text-[#475569] text-sm mb-8 italic text-center">
            Each script includes: the people-pleasing response you're used to, the boundary language you GET TO use instead, a softer stepping stone if you're not ready for the full version, and an affirmation to remind you why this matters.
          </p>

          {scripts.map((script) => (
            <ScriptCard key={script.id} script={script} locked={!unlocked && script.id > 3} />
          ))}
        </div>

        {/* Lead magnet — appears after 3 free scripts */}
        <LeadMagnet
          storageKey="boundary-builder-unlocked"
          source="boundary-builder-lead-magnet"
          formId="9350007"
          title="You deserve all 20 boundary scripts."
          pitch="Scripts 1–3 are just the start. Enter your email to unlock the other 17 scripts, the family conversation guide, and the full affirmation practice — all free."
          bullets={[
            "17 more scripts (Family, Friends, Relationship, Social, Self)",
            "Complete family boundary conversation guide (6 scripts)",
            "12 'I GET TO Say No' affirmations",
          ]}
          ctaLabel="✨ Unlock All 20 Scripts"
          unlockedTitle="Full Boundary Builder unlocked"
          unlockedCopy="Welcome. Take your time. One boundary at a time — you're already doing the brave work."
          theme="teal"
          unlocked={unlocked}
          markUnlocked={markUnlocked}
        />

        {/* PART 3: I GET TO Say No — Affirmation Practice */}
        <div className="mb-16 print:page-break-before relative" data-testid="affirmation-section">
          <div className={!unlocked ? "blur-sm select-none pointer-events-none print:blur-0" : ""}>
            <div className="bg-[#068fa2] rounded-2xl p-6 mb-8">
              <p className="text-[#5EEAD4] text-sm font-bold uppercase tracking-widest mb-1">Part 3</p>
              <h2 className="text-white text-2xl md:text-3xl font-serif">"I GET TO Say No" — Affirmation Practice</h2>
              <p className="text-white/60 text-sm mt-2">Read one each morning. Say it out loud. Write it on a sticky note. Let it rewire the guilt.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {iGetToAffirmations.map((affirmation, i) => (
                <div
                  key={i}
                  className="bg-gradient-to-br from-[#068fa2] to-[#25a1c2] rounded-xl p-6 flex items-center justify-center text-center min-h-[120px]"
                >
                  <p className="font-serif text-white italic leading-relaxed text-sm md:text-base">"{affirmation}"</p>
                </div>
              ))}
            </div>

            <div className="mt-8 bg-white rounded-2xl p-6 md:p-8 border border-[#CCFBF1]">
              <h3 className="font-serif text-xl text-[#25a1c2] mb-4">Write Your Own "I GET TO" Boundary Affirmations:</h3>
              <p className="text-[#475569] text-sm mb-4">What do YOU need to give yourself permission for? Write 3 of your own below.</p>
              <div className="space-y-4">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="flex items-start gap-3">
                    <span className="text-[#25a1c2] font-bold text-sm mt-1">{n}.</span>
                    <div className="flex-1">
                      <p className="text-[#25a1c2] text-sm font-medium mb-1">I GET TO...</p>
                      <div className="h-10 border-b-2 border-[#CCFBF1]" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {!unlocked && <LockedBadge label="Unlock 12 affirmations" />}
        </div>

        {/* PART 4: Family Boundary Conversation Guide */}
        <div className="mb-16 print:page-break-before relative" data-testid="family-guide-section">
          <div className={!unlocked ? "blur-sm select-none pointer-events-none print:blur-0" : ""}>
            <div className="bg-gradient-to-r from-[#B45309] to-[#D97706] rounded-2xl p-6 mb-8">
              <p className="text-white/70 text-sm font-bold uppercase tracking-widest mb-1">Part 4</p>
              <h2 className="text-white text-2xl md:text-3xl font-serif">Family Boundary Conversation Guide</h2>
              <p className="text-white/60 text-sm mt-2">The hardest boundaries are with the people closest to us. Here's how to have the conversation.</p>
            </div>

          <div className="bg-white rounded-2xl p-6 md:p-8 border border-[#CCFBF1] mb-6">
            <h3 className="font-serif text-lg text-[#042F2E] mb-4">Before You Begin — Read This:</h3>
            <div className="space-y-3 text-[#068fa2] text-sm leading-relaxed">
              <p>Setting boundaries with family is the hardest thing most people will ever do. It feels like betrayal. It feels like you're being mean. It feels like you're breaking something.</p>
              <p>But here's the truth: <strong>you are not breaking anything. You are repairing yourself.</strong></p>
              <p>The people who love you will adjust. The people who don't will resist. Both responses give you information you need.</p>
              <p className="text-[#25a1c2] font-medium italic">These scripts are starting points. Edit them. Soften them. Make them yours. The most powerful boundary is one that sounds like YOU.</p>
            </div>
          </div>

          <div className="space-y-6">
            {familyConversationStarters.map((item, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#CCFBF1] overflow-hidden shadow-sm">
                <div className="bg-[#F0F5F4] px-6 py-3 flex items-center gap-2">
                  <ChevronRight className="w-4 h-4 text-[#25a1c2]" />
                  <span className="text-[#25a1c2] font-bold text-sm">{item.topic}</span>
                </div>
                <div className="p-6">
                  <div className="bg-[#F0FDF4] border border-[#25a1c2]/20 rounded-xl p-4 mb-4">
                    <p className="text-[#068fa2] font-medium text-sm leading-relaxed">{item.script}</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Pen className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                    <p className="text-gray-500 text-xs italic">{item.tip}</p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-dashed border-gray-200">
                    <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-2">Rewrite in your own words:</p>
                    <div className="h-12 border border-dashed border-gray-300 rounded-lg" />
                  </div>
                </div>
              </div>
            ))}
          </div>
          </div>
          {!unlocked && <LockedBadge label="Unlock family guide" />}
        </div>

        {/* Closing */}
        <div className="print:page-break-before" data-testid="closing-section">
          <div className="bg-[#042F2E] rounded-3xl p-8 md:p-12 text-center">
            <Shield className="w-12 h-12 text-[#5EEAD4] mx-auto mb-4" />
            <h2 className="font-serif text-2xl md:text-3xl text-white mb-4">You Are Not Mean. You Are Growing.</h2>
            <p className="text-white/70 text-sm leading-relaxed max-w-lg mx-auto mb-6">
              Every boundary you set is an act of self-love. Every "no" you speak is a "yes" to yourself.
              You are not losing people — you are finding yourself. And the right people? They'll still be there.
            </p>
            <div className="bg-white/10 rounded-xl p-6 max-w-md mx-auto mb-6">
              <p className="font-serif text-[#5EEAD4] italic text-lg">"I GET TO protect my peace. I GET TO choose me. I GET TO build relationships that honor who I am becoming."</p>
            </div>
            <p className="text-white/40 text-xs mt-4">mindbodyspirithygienetools.com — By Temple Franklin & the Divine Team</p>

            <div className="print:hidden mt-8">
              <Link
                to="/"
                className="inline-flex items-center gap-2 bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-full px-8 py-4 font-medium transition-all hover:-translate-y-1 shadow-lg"
                data-testid="back-to-shop-bottom"
              >
                Explore More Tools
              </Link>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center pt-8 mt-8 border-t border-[#CCFBF1]">
          <p className="text-[#25a1c2] font-medium text-sm">The Boundary Builder — Mind Body Spirit HYGIENE™ Tools</p>
          <p className="text-[#475569] text-xs mt-1">By Temple Franklin & the Divine Team</p>
          <p className="text-[#475569] text-xs mt-1">mindbodyspirithygienetools.com</p>
        </div>
      </div>
    </div>
  );
}
